import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { findCustomers, saveCustomer } from "../services/sales";
import { addToSuggestions, CustomerSuggestion } from "../suggestions";
import {
  showErrorNotification,
  showSuccessNotification,
} from "./notifications";

const customersAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialStateEdit = {
  id: null,
  isNew: false,
  isSaving: false,
};

const initialState = {
  ...customersAdapter.getInitialState(),
  edit: initialStateEdit,
};

const customersSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    edit: (state, { payload }) => {
      state.edit.id = payload.id;
      state.edit.isNew = !!payload.isNew;
    },
    cancelEdit: (state) => ({ ...state, edit: initialStateEdit }),
    setCustomers: customersAdapter.setAll,
  },
  extraReducers: (builder) => {
    builder
      .addCase(save.fulfilled, (state, { payload }) =>
        customersAdapter.setOne({ ...state, edit: initialStateEdit }, payload),
      )
      .addCase(save.pending, (state) => {
        state.edit.isSaving = true;
      })
      .addCase(save.rejected, (state) => {
        state.edit.isSaving = false;
      });
  },
});

export default customersSlice.reducer;

export const { cancelEdit, edit, setCustomers } = customersSlice.actions;

export const isValidCustomer = (customer) =>
  customer?.name?.length >= 3 && !!customer.serviceFee;

export const selectors = customersAdapter.getSelectors(
  (state) => state.customers,
);

export const selectEditedCustomerId = (state) => state.customers.edit.id;

export const selectEditedCustomerIsNew = (state) =>
  !!state.customers.edit.isNew;

export const selectCustomerEditOpen = (state) => !!state.customers.edit.id;

export const selectCustomerIsSaving = (state) => state.customers.edit.isSaving;

export const loadCustomers = createAsyncThunk(
  "customers/loadAll",
  (_, { dispatch }) =>
    findCustomers().then((customers) => {
      addToSuggestions(customers.map(CustomerSuggestion));
      dispatch(setCustomers(customers));
    }),
);

const API_ERRORS = {
  400: "You provided incomplete or invalid customer data",
};

export const save = createAsyncThunk(
  "customers/save",
  (customer, { dispatch, getState }) => {
    const id = selectEditedCustomerId(getState());
    const isNewCustomer = selectEditedCustomerIsNew(getState());
    const payload = {
      ...customer,
      id,
    };
    return saveCustomer(payload)
      .then((savedCustomer) => {
        if (isNewCustomer) {
          addToSuggestions([CustomerSuggestion(savedCustomer)]);
        }
        dispatch(showSuccessNotification("Customer saved successfully"));
        return savedCustomer;
      })
      .catch((err) => {
        const message = API_ERRORS[err.status] || err.message;
        dispatch(showErrorNotification(message));
        throw err;
      });
  },
);
